import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '@core/components/base-component';
import { Estimate } from '@interfaces/Estimate';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { ProblematicSubLevelsDto } from '@interfaces/ProblematicSubLevelsDto';
import { EstimateService } from '@shared/services/estimate.service';
import { MediaService } from '@shared/services/media.service';
import { ProjectTotals } from '@shared/services/models/project-totals.model';
import { TotalsDataService } from '@shared/services/total.service';
import { combineLatest, startWith } from 'rxjs';

@Component({
  selector: 'pop-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class ToolbarComponent extends BaseComponent implements OnInit {
  public currentEstimate?: Estimate;
  public disableTooltip = true;
  public marginFees: number;
  public marginFeesPerc: number;
  public marginFinal: number;
  public marginFinalPerc: number;
  public modalIsOpen = false;
  public plannedMarkupRate: number;
  public problematicSublevels: ProblematicSubLevelsDto;
  private totalCost: number;

  @Input() public departmentLabel: string;
  @Input() public isGroupApproved: boolean;
  @Input() public hasCRM: boolean;
  @Input() public settings: EstimateConfiguration;

  public constructor(
    public dialog: MatDialog,
    private totalsData: TotalsDataService,
    private estimateService: EstimateService,
    private mediaService: MediaService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    super();
  }

  public ngOnInit() {
    this.subscribe(
      combineLatest([
        this.estimateService.observeCurrentEstimate(),
        this.totalsData.currentProjectTotals$.pipe(startWith({})),
        this.mediaService.getPageSize(),
        this.estimateService.observeAlreadyDeployedSubLevels(),
        this.estimateService.treetableHasChanges().pipe(startWith(undefined))
      ]).subscribe(([currentEstimate, projectTotals, size, alreadyDeployedSubLevels]) => {
        this.currentEstimate = currentEstimate;
        this.problematicSublevels = alreadyDeployedSubLevels;

        if (size === 'xs' || size === 'sm' || size === 'md') {
          this.disableTooltip = false;
        } else {
          this.disableTooltip = true;
        }
        if (this.currentEstimate.rate === null) {
          this.currentEstimate.rate = 0;
        }
        this.getTotals(projectTotals);
      })
    );

    this.detectModalShouldBeOpen();
    this.subscribe(
      this.router.events.subscribe(routerEvent => {
        if (routerEvent instanceof NavigationEnd) {
          this.detectModalShouldBeOpen();
        }
      })
    );
  }

  private detectModalShouldBeOpen() {
    this.modalIsOpen = this.activatedRoute.children.length > 0;
  }

  private getTotals(projectTotals: ProjectTotals) {
    this.totalCost = projectTotals.totalHoursCost + projectTotals.totalExpensesCost;
    this.marginFinal = projectTotals.totalBillableFinal - this.totalCost;
    this.marginFinalPerc =
      projectTotals.totalBillableFinal === 0 ? 0 : this.marginFinal / projectTotals.totalBillableFinal;
    this.marginFees = projectTotals.totalHoursBillableFinal - projectTotals.totalHoursCost;
    this.marginFeesPerc =
      projectTotals.totalHoursBillableFinal === 0 ? 0 : this.marginFees / projectTotals.totalHoursBillableFinal;
    this.plannedMarkupRate =
      projectTotals.totalHoursCost === 0 ? 0 : projectTotals.totalHoursBillable / projectTotals.totalHoursCost;
  }

  private async openDialog(route: string) {
    await this.router.navigate([route], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge' // remove to replace all query params by provided
    });
  }

  public async closeModal() {
    const closeDialogPromise = this.openDialog('.');
    const saveChangesPromise = this.estimateService.saveChangesAsync();
    await Promise.all([closeDialogPromise, saveChangesPromise]);
    this.estimateService.dialogEmitChanges();
  }

  public async openParameterDialog() {
    await this.openDialog('properties');
  }

  public async openDepartmentDialog() {
    await this.openDialog('department');
  }

  public async openStructureDialog() {
    await this.openDialog('structure');
  }

  public async openClassificationDialog() {
    await this.openDialog('class');
  }

  public async openInternalResourcesDialog() {
    await this.openDialog('internal-resources');
  }
}
